import React from 'react';
import { View, StyleSheet } from 'react-native';
import { AIBadge, Text } from '~/components/core';
import { useAIStrategyEnabled, useAppSelector, useStyles } from '~/hooks';
import { appSettingsSelector } from '~/state/selectors';
import { FONT_SIZE_XS, FONT_SIZE_S } from '~/constants';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';
import { Commission } from '~/api-services/ReportsService/types';
import CommissionDetails from './CommissionDetails';

type Props = {
    commission: Commission;
    isOpen: boolean;
};

export default function CommissionRow({ commission, isOpen }: Props) {
    const styles = useStyles(stylesGenerator);
    const { colorScheme } = useAppSelector(appSettingsSelector);
    const aiStrategyEnabled = useAIStrategyEnabled();
    return (
        <>
            <View
                style={[
                    styles.positionGroupContainer,
                    isOpen ? styles.positionGroupOpen : styles.positionGroupClose,
                    colorScheme === 'dark' ? styles.borderColorDark : styles.borderColorWhite,
                ]}
                key={'instrumentContainer' + commission.symbol}
            >
                <View
                    style={[
                        styles.positionItemSide,
                        colorScheme === 'dark' ? styles.borderColorDark : styles.borderColorWhite,
                    ]}
                >
                    <View style={[styles.rotateTextWrapper, styles.buySell]}>
                        <Text
                            fontType="LATO_BLACK"
                            style={commission.side === 'buy' ? styles.buyText : styles.sellText}
                        >
                            {commission.side.toUpperCase()}
                        </Text>
                    </View>
                </View>
                <View style={styles.instrumentName}>
                    <Text fontType="LATO_BOLD" style={styles.instrumentNameText}>
                        {commission.symbolNameTranslated}
                    </Text>
                    {commission.strategyId && aiStrategyEnabled ? <AIBadge /> : null}
                </View>
                <View style={styles.positionGroupVolume}>
                    <View style={styles.volumeBackground}>
                        <Text fontType="LATO_BOLD" style={styles.volumeText}>
                            {commission.volumeFormatted}
                        </Text>
                    </View>
                </View>
                <View style={styles.atSeparatorMargin}>
                    <Text style={styles.atSeparatorText}>@</Text>
                </View>
                <View style={styles.positionItemPrice}>
                    <Text style={[styles.positionItemPriceText, styles.positionItemPriceRight]}>{''}</Text>
                </View>
                <View style={styles.positionItemStatus}>
                    <Text style={[styles.positionItemPriceText, styles.positionItemPriceRight]}>
                        {commission.amountFormatted}
                    </Text>
                </View>
            </View>
            {isOpen ? <CommissionDetails commission={commission} /> : null}
        </>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        positionItemSide: {
            width: '5%',
            borderRightWidth: 1,
            justifyContent: 'center',
            alignItems: 'center',
        },
        instrumentName: { flexWrap: 'wrap', width: '22%', paddingLeft: '2%', justifyContent: 'center' },
        positionGroupVolume: { width: '21%', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' },
        atSeparatorMargin: { width: '10%', justifyContent: 'center', alignItems: 'center' },
        positionItemPrice: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', width: '20%' },
        positionItemStatus: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', width: '20%' },

        text_at: { color: theme.text_at },
        positionItemPriceText: { fontSize: FONT_SIZE_S, color: theme.text_pendingOrder },
        positionItemPriceRight: { paddingRight: scaleSize(7) },
        positionItemPriceLeft: { paddingLeft: scaleSize(7) },

        atSeparatorText: { color: theme.text_at, fontSize: FONT_SIZE_S },
        buyText: {
            fontSize: FONT_SIZE_S,
            color: theme.text_upPrice,
        },
        sellText: {
            fontSize: FONT_SIZE_S,
            color: theme.text_downPrice,
        },
        buySell: { width: scaleSize(50), alignItems: 'center' },
        rotateTextWrapper: { transform: [{ rotate: '-90deg' }] },

        positionGroupContainer: {
            flexDirection: 'row',
            borderWidth: 1,
            width: '100%',
            borderTopLeftRadius: scaleSize(5),
            borderTopRightRadius: scaleSize(5),
            height: scaleSize(50),
            backgroundColor: theme.backgroundSecondary,
        },
        positionGroupClose: { borderRadius: scaleSize(5), marginTop: scaleSize(7) },
        positionGroupOpen: {
            borderTopLeftRadius: scaleSize(5),
            borderTopRightRadius: scaleSize(5),
            marginTop: scaleSize(7),
        },
        borderColorDark: { borderColor: theme.background },
        borderColorWhite: { borderColor: theme.border_2 },

        instrumentNameText: { fontSize: FONT_SIZE_S, letterSpacing: 0, color: theme.text_symbol },
        volumeBackground: {
            backgroundColor: theme.background_message,
            borderRadius: scaleSize(2),
            flexDirection: 'row',
            paddingHorizontal: scaleSize(5),
            paddingVertical: scaleSize(3),
        },
        volumeText: { color: theme.white, fontSize: FONT_SIZE_XS, letterSpacing: 0.5 },
    });
